import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminApp } from './App';
import * as styled from './AppRouter.styled';
import { Address, AddressSearch } from './components/AddressSearch';
import { initAppmode, APPMODE } from './lib/appmode';

enum ParamName {
    UnitId = 'unitId',
    Street = 'unitStreet1',
    City = 'unitCity',
    State = 'unitState',
    Zip = 'unitZip',
    Country = 'unitCountry',
    MarketName = 'marketName',
}

export const AdminAppRouter: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const [unit, setUnit] = useState<string>();
    const [street, setStreet] = useState<string>();
    const [city, setCity] = useState<string>();
    const [state, setState] = useState<string>();
    const [zip, setZip] = useState<string>();
    const [country, setCountry] = useState<string>();
    const [hasAddress, setHasAddress] = useState(false);
    const [marketName, setMarketName] = useState<string>();

    // Extract values from query params
    useEffect(() => {
        const unit = params.get(ParamName.UnitId);
        const street = params.get(ParamName.Street);
        const city = params.get(ParamName.City);
        const state = params.get(ParamName.State);
        const zip = params.get(ParamName.Zip);
        const country = params.get(ParamName.Country);
        const marketName = params.get(ParamName.MarketName);

        setUnit(unit ?? undefined);
        setStreet(street ?? undefined);
        setCity(city ?? undefined);
        setState(state ?? undefined);
        setZip(zip ?? undefined);
        setCountry(country ?? undefined);
        setMarketName(marketName ?? undefined);
    }, [params]);

    // Check whether an address has been provided
    useEffect(() => {
        setHasAddress(!!street && !!city && !!state && !!zip && !!country);
    }, [street, city, state, zip, country]);

    // Sets the address in the url
    const handleAddress = (address: Address) => {
        params.set(ParamName.Street, address.street);
        params.set(ParamName.City, address.city);
        params.set(ParamName.State, address.state);
        params.set(ParamName.Zip, address.zip);
        params.set(ParamName.Country, address.country);
        setParams(params, { replace: false });
    };
    const { isAppMode } = initAppmode(unit);
    return (
        <styled.AppRouter
            id="app-router"
            className={isAppMode(APPMODE.ESTIMATE) ? 'integrated' : 'standalone'}
        >
            {!hasAddress ? (
                <AddressSearch
                    street={street}
                    city={city}
                    state={state}
                    zip={zip}
                    country={country}
                    onAddress={handleAddress}
                />
            ) : (
                <AdminApp
                    unitId={unit}
                    street={street!}
                    city={city!}
                    state={state!}
                    zip={zip!}
                    country={country!}
                    marketName={marketName!}
                />
            )}
        </styled.AppRouter>
    );
};
